<script setup>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { AppMenuPanel } from '@/components/molecules/AppMenuPanel'
import AppEmptyState from '@/components/molecules/AppEmptyState'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import NotificationTopMenu from '@/views/Notifications/components/NotificationTopMenu.vue'
import NotificationItems from '@/views/Notifications/components/NotificationItems.vue'
import { useNotificationsStore } from '@/stores/NotificationsStore'
import bellIcon from '@/assets/images/icons/bell.svg'
import bellFlatIcon from '@/assets/images/icons/bell_flat.svg'

const notificationsStore = useNotificationsStore()
const route = useRoute()

const open = ref(false)
const refreshing = ref(false)

const isEmpty = computed(() =>
  !notificationsStore.preparedInprogressJobs.length
  && !notificationsStore.preparedNotificationsPreviews?.length
  && !notificationsStore.preparedUnreadNotifications.length
)

// "All caught up" is a claim about the data, so it is only made once the
// requests have answered. Before that the panel spins; if they failed it says
// so — otherwise a non-zero badge sits above an empty-state that contradicts it.
const loadFailed = computed(() =>
  notificationsStore.notificationsError || notificationsStore.notificationsPreviewsError
)

const refresh = async () => {
  refreshing.value = true
  // The store actions swallow their own errors, so this always settles.
  await Promise.all([
    notificationsStore.fetchInprogressJobs(),
    notificationsStore.fetchNotificationsPreviews(),
    notificationsStore.fetchNotifications(),
    // Re-read the badge too: it was fetched once at header mount and would
    // otherwise disagree with the list just fetched.
    notificationsStore.fetchUnreadNotificationsCount(),
  ])
  refreshing.value = false
}

// Refresh the feed each time the panel opens.
watch(open, (isOpen) => {
  if (!isOpen) return
  refresh()
})

// Close on route change (was a `.v-popper__popper` DOM-removal hack under the
// old Flowbite tooltip — now a plain state flip).
watch(() => route.path, () => { open.value = false })
</script>

<template>
  <AppMenuPanel
    v-model:open="open"
    portal
    placement="bottom-end"
    width="w-[calc(100vw-16px)] sm:w-96"
    extra-class="shadow-xl"
  >
    <template #trigger>
      <button
        type="button"
        class="relative w-6 h-6 md:w-8 md:h-8 xl:hover:bg-blue-600 rounded flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-300"
        @click="open = !open"
      >
        <img :src="bellIcon" class="h-6 w-6" alt="Notifications" />
        <span
          v-if="notificationsStore.unreadNotificationsCount"
          class="absolute bottom-1 right-0 w-fit px-1 bg-red-500 rounded-full flex items-center justify-center"
        >
          <span class="text-[10px] font-semibold text-white">{{ notificationsStore.unreadNotificationsCount }}</span>
        </span>
      </button>
    </template>

    <!-- Header (fixed above the scrolling feed) -->
    <header class="flex-none bg-white border-b border-gray-200 py-2 px-3 flex items-center justify-between">
      <h2 class="text-lg font-semibold leading-[150%] text-gray-900">Notifications</h2>
      <NotificationTopMenu />
    </header>

    <!-- Feed -->
    <div class="max-h-[440px] overflow-y-auto">
      <div
        v-if="refreshing && isEmpty"
        class="flex justify-center items-center h-[390px]"
      >
        <LoadingSpinner :background="'bg-gray-100'" :width="'48px'" :height="'48px'" />
      </div>

      <AppEmptyState
        v-else-if="isEmpty && loadFailed"
        variant="plain"
        size="sm"
        title="Couldn’t load notifications"
        action-label="Try again"
        action-variant="secondary"
        class="h-[390px]"
        @action="refresh"
      >
        <template #image>
          <img :src="bellFlatIcon" alt="" class="mb-2" />
        </template>
      </AppEmptyState>

      <AppEmptyState
        v-else-if="isEmpty"
        variant="plain"
        title="You’re all caught up"
        class="h-[390px]"
      >
        <template #image>
          <img :src="bellFlatIcon" alt="" class="mb-2" />
        </template>
      </AppEmptyState>

      <template v-else>
        <NotificationItems getter="preparedInprogressJobs" type="job" :readable-state="false" />
        <NotificationItems getter="preparedNotificationsPreviews" type="preview" />
        <NotificationItems getter="preparedUnreadNotifications" type="notification" />
      </template>
    </div>
  </AppMenuPanel>
</template>
